import React from 'react';
import { Box, Text, VStack, HStack, Icon } from '@chakra-ui/react';
import { FiZap } from 'react-icons/fi';
import ModalShell from '../../../../../../components/ui/ModalShell';
import { ff } from '../../../../../../theme';
import LocalCreatorsBrandMark from '../LocalCreatorsBrandMark';
import { HOW_IT_WORKS_FIGMA } from '../../constants';
import { HOW_IT_WORKS_STEPS } from '../../howItWorksSteps';

export interface WelcomeLocalCreatorsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WELCOME_INTRO = [
  "You've been hand-picked as one of the best",
  "creators in your city. Here's how brands will",
  'find and book you.',
] as const;

const WelcomeLocalCreatorsModal: React.FC<WelcomeLocalCreatorsModalProps> = ({
  isOpen,
  onClose,
}) => (
  <ModalShell
    isOpen={isOpen}
    onClose={onClose}
    hideClose
    primary={{ label: 'Set up my profile', onClick: onClose, tone: 'lime' }}
  >
    <VStack align="stretch" spacing={0}>
      <Box mx="auto">
        <LocalCreatorsBrandMark size={60} />
      </Box>

      <Text as="h2" textStyle="display" textAlign="center" fontSize="26px" color={ff.ink} mt="16px" px="2">
        Welcome to Local Creators
      </Text>

      <HStack
        h="26px"
        mt="10px"
        px="10px"
        borderRadius="full"
        bg={ff.ink}
        spacing="6px"
        justify="center"
        alignSelf="center"
        maxW="100%"
        fontFamily="display"
      >
        <Icon as={FiZap} w="12px" h="12px" color={ff.lime} flexShrink={0} aria-hidden />
        <Text as="span" fontSize="12px" fontWeight={600} color={ff.lime}>
          Top 1%
        </Text>
        <Text as="span" fontSize="12px" color={ff.muted}>
          ·
        </Text>
        <Text as="span" fontSize="12px" fontWeight={600} color="white">
          Invite-only
        </Text>
      </HStack>

      <VStack align="center" spacing={0} mt="14px" px="2">
        {WELCOME_INTRO.map((line) => (
          <Text key={line} textAlign="center" fontSize="15px" lineHeight="22px" color={ff.body}>
            {line}
          </Text>
        ))}
      </VStack>

      <Box
        w="100%"
        borderTop="1px solid"
        borderColor={ff.line}
        mt="18px"
        pt="16px"
        role="list"
        aria-label="How Local Creators works"
      >
        {HOW_IT_WORKS_STEPS.map((step, index) => {
          const { Icon: StepIcon } = step;
          return (
            <Box key={step.title} role="listitem" position="relative" minH={HOW_IT_WORKS_FIGMA.rowH} w="100%">
              {index < 3 && (
                <Box
                  position="absolute"
                  left={HOW_IT_WORKS_FIGMA.lineLeft}
                  top={HOW_IT_WORKS_FIGMA.lineFromIconBottom}
                  bottom={HOW_IT_WORKS_FIGMA.lineToRowBottom}
                  w="1px"
                  bg={ff.border}
                  zIndex={0}
                  pointerEvents="none"
                  aria-hidden
                />
              )}
              <Box
                position="absolute"
                left="0"
                top={HOW_IT_WORKS_FIGMA.iconOffsetTop}
                w={HOW_IT_WORKS_FIGMA.icon}
                h={HOW_IT_WORKS_FIGMA.icon}
                borderRadius="full"
                bg={ff.lime}
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={1}
                color={ff.ink}
                aria-hidden
              >
                <StepIcon size={15} />
              </Box>
              <Box pl={HOW_IT_WORKS_FIGMA.textLeft} display="flex" flexDirection="column" pt="1px">
                <Text as="div" fontFamily="display" fontSize="15px" fontWeight={600} lineHeight="1.25" color={ff.ink}>
                  {step.title}
                </Text>
                <Text as="div" fontSize="13px" lineHeight="1.4" color={ff.text2} pr={{ base: 0, sm: 2 }}>
                  {step.body}
                </Text>
              </Box>
            </Box>
          );
        })}
      </Box>
    </VStack>
  </ModalShell>
);

export default WelcomeLocalCreatorsModal;
